import React from 'react';
import styled from 'styled-components';
import Polygon from 'assets/Polygon.svg';
import PolygonActive from 'assets/PolygonActive.svg';

export interface TriggerStyleConfig {
    minWidth?: string;
    width?: string;
    height?: string;
    fontSize?: string;
    padding?: string;
    borderRadius?: string;
    selectedBackground?: string;
    selectedBorder?: string;
    normalBorder?: string;
    clearIconSize?: string;
}

interface DateRangeTriggerProps {
    selectedData: string;
    isOpen: boolean;
    onClick: () => void;
    onClear?: () => void;
    triggerRef?: React.RefObject<HTMLDivElement | null>;
    styleConfig?: TriggerStyleConfig;
    placeholderText?: string;
    showClearButton?: boolean;
}

const PLACEHOLDER_TEXT = "Select a period";

export const DateRangeTrigger: React.FC<DateRangeTriggerProps> = (props) => {
    const {
        selectedData,
        isOpen,
        onClick,
        onClear,
        triggerRef,
        styleConfig = {},
        placeholderText = PLACEHOLDER_TEXT,
        showClearButton = true,
    } = props;

    const hasSelectedDate = selectedData !== placeholderText;

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClear?.();
    };

    return (
        <TriggerButton
            ref={triggerRef}
            onClick={onClick}
            className={hasSelectedDate ? 'has-selected-date' : ''}
            $styleConfig={styleConfig}
        >
            <span>{selectedData}</span>
            <IconWrapper>
                {hasSelectedDate && showClearButton && (
                    <ClearIcon
                        onClick={handleClear}
                        $size={styleConfig.clearIconSize}
                    >
                        ×
                    </ClearIcon>
                )}
                {(!hasSelectedDate || !showClearButton) && (
                    !isOpen ? <Polygon /> : <PolygonActive />
                )}
            </IconWrapper>
        </TriggerButton>
    );
};

const defaultStyles = {
    minWidth: '160px',
    width: '100%',
    height: '30px',
    fontSize: '15px',
    padding: '0 12px',
    borderRadius: '8px',
    selectedBackground: '#E2E5FF',
    selectedBorder: '#E2E5FF',
    normalBorder: '#A4A4A4',
    clearIconSize: '11px',
}

export const DEFAULT_TRIGGER_STYLE: Required<TriggerStyleConfig> = defaultStyles;

export const ANALYTICS_TRIGGER_STYLE: TriggerStyleConfig = defaultStyles;

export const FINANCE_TRIGGER_STYLE: TriggerStyleConfig = defaultStyles;

const TriggerButton = styled.div<{ $styleConfig: TriggerStyleConfig }>`
    width: ${props => props.$styleConfig.width || DEFAULT_TRIGGER_STYLE.width};
    min-width: ${props => props.$styleConfig.minWidth || DEFAULT_TRIGGER_STYLE.minWidth};
    height: ${props => props.$styleConfig.height || DEFAULT_TRIGGER_STYLE.height};
    border-radius: ${props => props.$styleConfig.borderRadius || DEFAULT_TRIGGER_STYLE.borderRadius};
    border: 1px solid ${props => props.$styleConfig.normalBorder || DEFAULT_TRIGGER_STYLE.normalBorder};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 5px;
    padding: ${props => props.$styleConfig.padding || DEFAULT_TRIGGER_STYLE.padding};
    background: white;
    font-size: ${props => props.$styleConfig.fontSize || DEFAULT_TRIGGER_STYLE.fontSize};
    transition: background 0.15s, border 0.15s;
    color: #000000;

    &.has-selected-date {
        background: ${props => props.$styleConfig.selectedBackground || DEFAULT_TRIGGER_STYLE.selectedBackground};
        border: 1px solid ${props => props.$styleConfig.selectedBorder || DEFAULT_TRIGGER_STYLE.selectedBorder};
    }

    span {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    svg {
        flex-shrink: 0;
    }

    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
`;

const ClearIcon = styled.span<{ $size?: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${props => props.$size || DEFAULT_TRIGGER_STYLE.clearIconSize};
    height: ${props => props.$size || DEFAULT_TRIGGER_STYLE.clearIconSize};
    line-height: 1;
    color: #000000;
    cursor: pointer;
    transition: color 0.15s;
    font-size: 16px;
    font-weight: 600;
    font-family: 'Open Sans';

    ${props => props.$size && parseInt(props.$size) > 15 && `
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.1);
        
        &:hover {
            background: rgba(0, 0, 0, 0.2);
        }
    `}
`;
